/** Simple wrapper around the Arya library providing a type for working with the scene  */
import { AryaAdaptor, AryaEvent, AryaRoom, Mode } from '@digital-bridge/artisan-arya';
import { LanguageJSON } from '@digital-bridge/conduit';
import { Orientation3D, Position3D } from '@digital-bridge/fine';
import { pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import { FurnitureDescription } from '../../../data/furnitureDescription';
import * as DataPose from '../../../data/pose';
import { Pose } from '../../../data/pose';
import * as Room from '../../../data/room';
import * as RoomFixtures from '../../../fixtures/room';

export type Scene = AryaAdaptor;

/**
 * Add a single piece of furniture, with an optional pose.
 * AutoDisplay parameter determines whether the Furniture is displayed when ready, or if it remains hidden
 */
export const addFurniture =
    (fd: FurnitureDescription, pose?: Pose, autoDisplay: boolean = true) =>
    async (scene: Scene) => {
        const furniture = await scene.addFurniture(fd);
        if (pose) {
            const position = DataPose.position(pose);
            const orientation = DataPose.orientation(pose);
            furniture.setPositionWorld(position, orientation);
        } else {
            furniture.setPositionWorld(new Position3D(0, 0, 0), Orientation3D.identity());
        }

        const id = furniture.getFurnitureHash();
        if (autoDisplay) {
            scene.intents().showFurniture(id);
        }

        return furniture.getPersistentID();
    };

export const removeFurniture = (furnitureId: string) => (scene: Scene) =>
    scene.intents().deleteFurnitureByFurnitureId(furnitureId);

export const addFurnitureGroup =
    (fs: { description: FurnitureDescription; pose: Pose }[], pose: Pose) =>
    async (scene: Scene) => {
        const furnitureIds = await Promise.all(
            fs.map(({ description, pose }) => pipe(scene, addFurniture(description, pose, false))),
        );

        // If any fail, delete any that added and return failure
        if (furnitureIds.some(id => id === undefined)) {
            furnitureIds.forEach(id => {
                if (id !== undefined) {
                    pipe(scene, removeFurniture(id));
                }
            });
            return Promise.reject(
                'at least some furniture in the group failed to add to Arya, rolling back entire group',
            );
        }

        return await scene
            .groupFurniture(
                furnitureIds.filter(id => id) as Array<string>,
                DataPose.toAryaTransform(pose),
            )
            .then(groupId => {
                /** Show furniture AFTER sofa is positioned */
                furnitureIds.forEach(id => {
                    if (id !== undefined) {
                        scene.intents().showFurnitureById(id);
                    }
                });

                return groupId;
            });
    };

export const removeFurnitureGroup = (groupId: string) => (scene: Scene) => {
    scene.intents().deleteGroupContents(groupId);
};

export const listFurniture = (scene: Scene) => scene.queries().furnitureIds();

export const furnitureSummaries = (scene: Scene) => scene.queries().furnitureSummaries();

export const selectFurniture = (furnitureId: string) => (scene: Scene) =>
    scene.intents().selectFurnitureByFurnitureId(furnitureId);

export const selectedFurniture = (scene: Scene) => scene.queries().selectedFurnitureIds();

export const furniturePose =
    (furnitureId: string) =>
    (scene: Scene): O.Option<Pose> =>
        pipe(
            scene.queries().furnitureTransformById(furnitureId),
            O.map(DataPose.fromAryaTransform),
        );

export const furnitureProducts = (furnitureId: string) => (scene: Scene) => {
    return scene.queries().furnitureProductIds(furnitureId);
};

export const furnitureReady = (furnitureId: string) => (scene: Scene) =>
    scene.queries().furnitureReady(furnitureId);

export const generateFurnitureGltf = (scene: Scene) => scene.queries().generateFurnitureGLTF();
export const generateFurnitureUsdz = (scene: Scene) => scene.queries().generateFurnitureUSDZ();

/**
 * Set or change the Room dimensions of the current Scene.
 * The room will be centered about the Origin before setting. This centered room is returned
 */
export const updateRoomDimensions = (room: AryaRoom) => (scene: Scene) => {
    const room_ = Room.recenter(room);
    pipe(
        scene.queries().room(),
        O.fold(
            () => {
                /** NO ROOM - so load this room */
                scene.loadRoom(room_, []);
            },
            () => {
                scene.intents().updateRoomDimensions(room_);
            },
        ),
    );
    return room_;
};

export const roomDimensions = (scene: Scene) => {
    return pipe(
        scene.queries().room(),
        O.getOrElse(() => RoomFixtures.basic),
    );
};

/** Set the Floor Covering  to one of a set of known coverings */
export const setFloorCovering = (coveringId: 'paving-new') => (scene: Scene) => {
    scene.addSerialisedCoverings([
        {
            coveringSpace: {
                guid: 'floor-covering',
                name: 'floor covering',
                description: '',
                categories: [],
                locale: LanguageJSON.LanguageTag.BritishEnglish,
                families: [],
                products: [
                    {
                        id: 0,
                        canonical_id: coveringId,
                        name: 'floor covering',
                        description: '',
                        thumbnail: '',
                        price: { currency: 'GBP', value: 0.0 },
                        properties: {},
                    },
                ],
            },
            coveringCanonicalId: coveringId,
            textureOrigin: { x: 0, y: 0, z: 0 },
            attachedTo: {
                position: { x: 0, y: 0, z: 0 },
            },
        },
    ]);
};

export const configurationTemplateId = (furnitureId: string) => (scene: Scene) => {
    return scene.queries().configurationTemplateId(furnitureId);
};

export const resetCamera = (scene: Scene) => scene.intents().resetOrbitCamera();

export const focusOnFurniture = (furnitureId: string) => (scene: Scene) =>
    scene.intents().focusOnFurniture(furnitureId);

export const updateFurniture =
    (furnitureId: string, furnitureDescription: FurnitureDescription) => (scene: Scene) => {
        scene
            .updateFurniture(furnitureId, furnitureDescription)
            .then(_ => scene.intents().showFurnitureById(furnitureId));
    };

export const setViewMode = (mode: Mode) => (scene: Scene) => scene.intents().changeView(mode);

export const onEvent = (event: AryaEvent, cb: (data: unknown) => void) => (scene: Scene) =>
    scene.events().on(event, cb);

export const offEvent = (event: AryaEvent, cb: (data: unknown) => void) => (scene: Scene) =>
    scene.events().off(event, cb);
